import { Skeleton } from "@credit-acceptance/uikit";
import { useTranslation } from "react-i18next";

import { ErrorState } from "../../components/ui/centeredMessage";
import { getErrorMessage } from "../../lib/query/errors";
import type {
  AggregatorValidation,
  AggregatorValidationHistoryEntry,
  AggregatorValidationItem,
} from "./api/aggregatorValidationTypes";
import { useAggregatorValidation } from "./api/useAggregatorValidation";
import AuditTable, { type AuditTableColumn } from "./AuditTable";
import { formatChangeDate } from "./formatChangeDate";

interface AggregatorValidationTabProperties {
  approvalId: string;
}

// Internal-tab Aggregator Validation
function AggregatorValidationTab({ approvalId }: AggregatorValidationTabProperties) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useAggregatorValidation(approvalId);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label={t("audit.aggregatorValidation.loading")}
        className="px-(--spacing-1) py-(--spacing-3)"
      >
        <Skeleton loading active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={t("audit.aggregatorValidation.loadError", { message: getErrorMessage(error) })}
        className="p-0"
      />
    );
  }

  // No aggregator validation to show at all — a non-aggregator deal (and a
  // disabled query with no approvalId, see useAggregatorValidation) both
  // normalize to `aggregatorDeal: false`, so it's the reliable signal to gate
  // the whole tab on (root `validations` is empty on any passing attempt too,
  // since the BFF only returns mismatched fields there).
  if (!data || !data.aggregatorDeal) {
    return <ErrorState message={t("audit.aggregatorValidation.empty")} className="p-0" />;
  }

  const statusLabel = (failed: boolean) =>
    t(failed ? "audit.aggregatorValidation.status.failed" : "audit.aggregatorValidation.status.successful");

  // Expandable whenever there are field-level comparisons to show, matching
  // the history rows' convention below — not tied to hasFailedValidations,
  // since a passing attempt has no mismatched fields to drill into.
  const isCurrentRowExpandable = data.validations.length > 0;

  const historyRows = data.history;

  const currentColumns: Array<AuditTableColumn<AggregatorValidation>> = [
    {
      key: "status",
      header: t("audit.aggregatorValidation.columns.currentStatus"),
      render: (row) => statusLabel(row.hasFailedValidations),
      className: isCurrentRowExpandable ? "font-(--font-weight-semibold)" : undefined,
    },
    {
      key: "dateTime",
      header: t("audit.aggregatorValidation.columns.dateTime"),
      render: (row) => formatChangeDate(row.lastUpdatedDatetime),
    },
  ];

  const historyColumns: Array<AuditTableColumn<AggregatorValidationHistoryEntry>> = [
    {
      key: "status",
      header: t("audit.aggregatorValidation.columns.historyStatus"),
      render: (row) => statusLabel(row.hasFailedValidations),
      className: "font-(--font-weight-semibold)",
    },
    {
      key: "dateTime",
      header: t("audit.aggregatorValidation.columns.dateTime"),
      render: (row) => formatChangeDate(row.lastUpdatedDatetime),
    },
  ];

  // capsValue/aggregatorValue can carry full sentences (e.g. a validation
  // error message) rather than a short value, so they're wrapped and
  // width-capped instead of stretching the table to fit one long line.
  const historyDetailColumns: Array<AuditTableColumn<AggregatorValidationItem>> = [
    {
      key: "fieldName",
      header: t("audit.aggregatorValidation.columns.fieldName"),
      render: (row) => row.fieldName,
      className: "align-top",
    },
    {
      key: "capsValue",
      header: t("audit.aggregatorValidation.columns.capsValue"),
      render: (row) => row.capsValue,
      className: "max-w-xs break-words align-top",
    },
    {
      key: "aggregatorValue",
      header: t("audit.aggregatorValidation.columns.aggregatorValue"),
      render: (row) => row.aggregatorValue,
      className: "max-w-xs break-words align-top",
    },
  ];

  return (
    <div className="gap-(--spacing-4) flex h-full flex-col">
      <div className="gap-(--spacing-3) flex flex-col">
        <h3 className="font-(--font-weight-semibold) text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height)">
          {t("audit.aggregatorValidation.verificationDetailsTitle")}
        </h3>
        <AuditTable
          caption={t("audit.aggregatorValidation.captions.current")}
          columns={currentColumns}
          rows={[data]}
          stickyHeader
          // Accordion is gated on status: expandable only when failed, matching the
          // history table's null-to-disable convention below.
          renderExpandedRow={(row) =>
            isCurrentRowExpandable ? (
              <AuditTable
                caption={t("audit.aggregatorValidation.captions.historyDetail")}
                columns={historyDetailColumns}
                rows={row.validations}
              />
            ) : undefined
          }
        />
      </div>

      <div className="gap-(--spacing-2) flex min-h-0 flex-1 flex-col">
        <h3 className="font-(--font-weight-semibold) text-(length:--typography-base-sizes-small-font-size)/(--typography-base-sizes-small-line-height)">
          {t("audit.aggregatorValidation.history.title")}
        </h3>
        <p className="text-(--color-text-default-muted) text-xs">{t("audit.aggregatorValidation.history.hint")}</p>
        <AuditTable
          caption={t("audit.aggregatorValidation.captions.history")}
          columns={historyColumns}
          rows={historyRows}
          stickyHeader
          // Returning null marks the row non-expandable (see AuditTable):
          // an attempt with no field-level comparisons has nothing to show.
          renderExpandedRow={(entry) =>
            entry.validations.length === 0 ? null : (
              <AuditTable
                caption={t("audit.aggregatorValidation.captions.historyDetail")}
                columns={historyDetailColumns}
                rows={entry.validations}
              />
            )
          }
        />
      </div>
    </div>
  );
}

export default AggregatorValidationTab;
